<script setup lang="ts">
import { ref, watch } from "vue";
import ThreadsNav from "@/components/mobile/ThreadsNav.vue";
import MediaSelectPopup from "@/components/mobile/media/MediaSelectPopup.vue";
import { MediaUploadFileItem } from "@/types/component";
import {
  showConfirmDialog,
  showFailToast,
  showLoadingToast,
  showSuccessToast,
} from "vant";
import {
  MateSprite,
  MateSpriteAnimation,
  MateSpriteMode,
  MateSpriteStatus,
  MateSpriteTaskStatus,
} from "@/types/game";
import {
  createMateSpriteTask,
  readMateSprite,
  readMateSpriteTask,
  updateMateSprite,
  UpdateMateSpriteParams,
  replaceMateSpriteFrame,
} from "@/apis/script-game";
import { useI18n } from "vue-i18n";
import { MateSpriteDirection } from "@/enums/game";
import { Media } from "@/types/entity";
import { useGlobalModal } from "@/stores/globalModal";
import { useGlobalUser } from "@/stores/globalUser";
import { fixNumber } from "../Mission/MissionStat";
import { getAllFrameUrls } from "../Mate/Sprite/imgHandler";
import moment from "moment-timezone";
import { useMate } from "@/stores/mate";
import { useDungeon } from "@/stores/dungeon";

// Props
const props = defineProps<{
  show: boolean;
  sprite?: MateSprite;
  mateId: number;
}>();

const BASE_TIME = 25;
const TIME_INTERVAL = 3000;

// Emits
const emit = defineEmits<{
  (e: "update:show", value: boolean): void;
  (e: "saved"): void;
  (e: "frame-click"): void;
}>();

// State management
const modalStore = useGlobalModal();
const mateStore = useMate();
const userStore = useGlobalUser();
const showSingleFrameMediaSelectPopup = ref(false);
const isGenerating = ref(false);
const { t } = useI18n();
const selectedImage = ref<MediaUploadFileItem | null>(null);
const mateSprite = ref<MateSprite>({} as MateSprite);

const showSpriteEditor = ref(false);

const countDownPercent = ref(0);

const loadingDirection = ref<MateSpriteDirection>();
const loadingFrameIndex = ref<number>();
// Watch for sprite prop changes
watch(
  () => props.show,
  (show) => {
    if (show) {
      if (props.sprite) {
        mateSprite.value = JSON.parse(JSON.stringify(props.sprite));
        if (mateSprite.value.status === MateSpriteStatus.Processing) {
          startCountDown(3);
          isGenerating.value = true;
          pollTaskStatus();
        } else {
          resetCache();
          if (!mateSprite.value.prompt && mateSprite.value.referenceMedia) {
            selectedImage.value = {
              mediaId: mateSprite.value.referenceMedia!.id!,
              url: mateSprite.value.referenceMedia!.url!,
            };
          }
          initSort();
        }
      } else {
        resetState();
      }
    }
  }
);

// const canProceedToGeneration = computed(() => selectedImage.value !== null);

const singleFrameDirection = ref<MateSpriteDirection>();
const singleFrameFrameIndex = ref<number>();
const showSingleFrameEditor = ref(false);
const singleFrameImageUrls = ref<string[]>([]);

// const clickSingleFrameModify = (
//   spriteId: number,
//   direction?: MateSpriteDirection,
//   frameIndex?: number
// ) => {
//   singleFrameFrameIndex.value = frameIndex;

//   const animation = mateSprite.value.animationzz!.find(
//     (v) => v.direction === direction
//   );
//   if (!animation) return;
//   openSingleFrameEditor(animation.media!.url!, direction!);
// const options = [
//   {
//     name: t("sprite.uploadFrame"),
//     callback: async () => {
//       const isPass = await checkHasUnsaved(direction);
//       if (!isPass) return;

//       showSingleFrameMediaSelectPopup.value = true;
//       singleFrameDirection.value = direction;
//       singleFrameFrameIndex.value = frameIndex;
//     },
//   },
//   {
//     name: "像素编辑器",
//     callback: async () => {
//       singleFrameFrameIndex.value = frameIndex;

//       const animation = mateSprite.value.animationzz!.find(
//         (v) => v.direction === direction
//       );
//       if (!animation) return;
//       openSingleFrameEditor(animation.media!.url!, direction!);
//     },
//   },
// ];

// if (mateSprite.value.mode === MateSpriteMode.Image) {
//   options.unshift({
//     name: t("sprite.regenerate1"),
//     callback: () => createTask(spriteId, direction, frameIndex),
//   });
// }

// modalStore.openGlobalActionSheet(options);
// };

const openSingleFrameEditor = async (
  url: string,
  direction: MateSpriteDirection
) => {
  let imageWidth = props.sprite?.mode === MateSpriteMode.Image ? 192 : 384;
  const urls = await getAllFrameUrls(url, 48, imageWidth, 96);
  singleFrameImageUrls.value = urls;
  showSingleFrameEditor.value = true;
  singleFrameDirection.value = direction;
};

const onSingleFrameEditorSaved = async (media: Media) => {
  const animation = mateSprite.value.animationzz!.find(
    (v) => v.direction === singleFrameDirection.value
  );

  if (!animation) return;

  const res = await replaceMateSpriteFrame({
    animationId: animation.id!,
    mediaId: media.id!,
  });

  initSprint({
    animations: [
      {
        direction: singleFrameDirection.value!,
        media: res.data.animationMedia,
        mediaId: res.data.animationMedia.id!,
        spriteId: animation.spriteId!,
        action: animation.action,
      },
    ],
    imageMedia: res.data.imageMedia,
  });
};

const handleSingleFrameImageSelected = async (
  mediaList: MediaUploadFileItem[]
) => {
  unsavedDirection[singleFrameDirection.value!] = true;
  let [media] = mediaList;
  let height = media.media?.height;
  let width = media.media?.width;
  if (width !== 48 || height !== 96)
    return showFailToast(t("sprite.pleaseUpload48x96Image"));

  const animation = mateSprite.value.animationzz!.find(
    (v) => v.direction === singleFrameDirection.value
  );

  if (!animation) return;

  const res = await replaceMateSpriteFrame({
    animationId: animation.id!,
    frameIndex: singleFrameFrameIndex.value!,
    mediaId: media.mediaId!,
  });
  initSprint({
    animations: [
      {
        direction: singleFrameDirection.value!,
        media: res.data.animationMedia,
        mediaId: res.data.animationMedia.id!,
        spriteId: animation.spriteId!,
        action: animation.action,
      },
    ],
    imageMedia: res.data.imageMedia,
  });

  showSingleFrameMediaSelectPopup.value = false;
};

// 轮询任务状态
let timer: NodeJS.Timeout | null = null;
const pollTaskStatus = async () => {
  try {
    if (!mateSprite.value.uuid) {
      stopCountDown();
      isGenerating.value = false;
      return;
    }

    if (countDownPercent.value < 40) {
      timer = setTimeout(() => {
        pollTaskStatus();
      }, TIME_INTERVAL);
      return;
    }

    const task = await readMateSprite(mateSprite.value!.uuid!);
    if (task.item.status === MateSpriteStatus.Processing) {
      timer = setTimeout(() => {
        pollTaskStatus();
      }, TIME_INTERVAL);
    } else if (task.item.status === MateSpriteStatus.Completed) {
      isGenerating.value = false;
      mateSprite.value = task.item;
      initSort();
      stopCountDown();
      showSuccessToast(t("sprite.spriteGenerationCompleted"));
      emit("saved");
    } else if (task.item.status === MateSpriteStatus.Failed) {
      isGenerating.value = false;
      showFailToast(t("Dungeons.sprite.fail"));
    }
  } catch (e) {
    console.log(e);
    timer = setTimeout(() => {
      pollTaskStatus();
    }, TIME_INTERVAL);
  }
};

const initSort = () => {
  mateSprite.value.animationzz?.sort((a, b) => {
    // 按照 下右左上的顺序排列
    const order = [
      MateSpriteDirection.Down,
      MateSpriteDirection.Right,
      MateSpriteDirection.Left,
      MateSpriteDirection.Up,
    ];
    return order.indexOf(a.direction) - order.indexOf(b.direction);
  });
};

const resetState = () => {
  isGenerating.value = false;
  mateSprite.value = {} as MateSprite;
  resetCache();

  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (timer2) {
    clearTimeout(timer2);
    timer2 = null;
  }
  stopCountDown();
};

const resetCache = () => {
  loadingDirection.value = undefined;
  loadingFrameIndex.value = undefined;
  singleFrameDirection.value = undefined;
  singleFrameFrameIndex.value = undefined;

  Object.keys(unsavedDirection).forEach((d) => {
    unsavedDirection[Number(d) as MateSpriteDirection] = false;
  });
};

const taskUuid = ref<string>();
const unsavedDirection: Record<MateSpriteDirection, boolean> = {
  [MateSpriteDirection.Down]: false,
  [MateSpriteDirection.Right]: false,
  [MateSpriteDirection.Left]: false,
  [MateSpriteDirection.Up]: false,
};

const checkHasUnsaved = async (direction?: MateSpriteDirection) => {
  if (
    (direction && unsavedDirection[direction]) ||
    (!direction && unsavedDirection[MateSpriteDirection.Down])
  ) {
    try {
      await showConfirmDialog({
        message: t("sprite.unsavedOperation"),
        confirmButtonText: t("Buttons.confirm"),
        cancelButtonText: t("Buttons.cancel"),
      });
      return true;
    } catch {
      return false;
    }
  }
  return true;
};

const createTask = async (
  spriteId: number,
  direction?: MateSpriteDirection,
  frameIndex?: number
) => {
  const isPass = await checkHasUnsaved(direction);
  if (!isPass) return;

  if (!frameIndex || isPass) {
    try {
      await showConfirmDialog({
        message: direction
          ? t("sprite.confirmRegenerateDirection")
          : t("sprite.confirmRegenerateAll"),
        confirmButtonText: t("Buttons.confirm"),
        cancelButtonText: t("Buttons.cancel"),
      });
    } catch {
      return;
    }
  }

  loadingDirection.value = direction;
  loadingFrameIndex.value = frameIndex;

  if (direction) {
    if (
      modalStore.shouldOpenPowerNotEnoughModal({
        spentPowerAmount: 1,
        cb: () => createTask(spriteId, direction, frameIndex),
      })
    )
      return;
  } else {
    isGenerating.value = true;
  }

  try {
    if (direction) unsavedDirection[direction] = true;
    else {
      Object.keys(unsavedDirection).forEach((d) => {
        unsavedDirection[Number(d) as MateSpriteDirection] = true;
      });
    }

    const res = await createMateSpriteTask({
      spriteId,
      direction,
      frameIndex,
    });

    userStore.refresh();
    startCountDown(direction ? 1 : 3);

    taskUuid.value = res.data.taskId;
    pollTaskStatus2();
  } catch {
    console.log("createMateSpriteTask failed");
    isGenerating.value = false;
    loadingDirection.value = undefined;
    loadingFrameIndex.value = undefined;
  }
};

// 20秒
let countDownInterval: NodeJS.Timeout;
const startCountDown = (count = 1) => {
  stopCountDown();
  const secondAddPercent = 100 / (BASE_TIME * count);

  if (mateSprite.value.status === MateSpriteStatus.Processing) {
    const duration = moment().diff(
      moment(mateSprite.value.dtCreate),
      "seconds"
    );
    if (duration > 0) countDownPercent.value += secondAddPercent * duration;
  }

  countDownInterval = setInterval(() => {
    countDownPercent.value += secondAddPercent * (TIME_INTERVAL / 1000);
    if (countDownPercent.value >= 99) {
      countDownPercent.value = 99;
      clearInterval(countDownInterval);
    }
  }, TIME_INTERVAL);
};

const stopCountDown = () => {
  clearInterval(countDownInterval);
  countDownPercent.value = 0;
};

// 轮询任务状态
let timer2: NodeJS.Timeout | null = null;
const pollTaskStatus2 = async () => {
  try {
    if (countDownPercent.value < 40) {
      timer2 = setTimeout(() => {
        pollTaskStatus2();
      }, TIME_INTERVAL);
      return;
    }

    const task = await readMateSpriteTask(taskUuid.value!);
    if (task.data.status === MateSpriteTaskStatus.Processing) {
      timer2 = setTimeout(() => {
        pollTaskStatus2();
      }, TIME_INTERVAL);
    } else if (task.data.status === MateSpriteTaskStatus.Completed) {
      isGenerating.value = false;
      loadingDirection.value = undefined;
      stopCountDown();
      initSprint(task.data);
    } else if (task.data.status === MateSpriteTaskStatus.Failed) {
      isGenerating.value = false;
      loadingDirection.value = undefined;
      showFailToast(t("Dungeons.sprite.fail"));
    }
  } catch (e) {
    console.log(e);
    timer2 = setTimeout(() => {
      pollTaskStatus2();
    }, TIME_INTERVAL);
  }
};
const initSprint = (data: {
  animations: MateSpriteAnimation[];
  imageMedia: Media;
}) => {
  const { animations, imageMedia } = data;

  let directionMap = new Map<MateSpriteDirection, MateSpriteAnimation>();
  animations.forEach((animation) => {
    directionMap.set(animation.direction, animation);
  });

  mateSprite.value.animationzz?.forEach((v) => {
    const animation = directionMap.get(v.direction);
    if (animation) {
      v.media = animation.media;

      if (v.direction === MateSpriteDirection.Down) {
        mateSprite.value.imageMedia = imageMedia;
        mateSprite.value.imageMediaId = imageMedia.id!;
      }
    }
  });
  initSort();
};
const goBack = () => {
  emit("update:show", false);

  setTimeout(() => {
    resetState();
  }, 500);
};

const clickSave = async () => {
  const loading = showLoadingToast({
    message: t("sprite.saving"),
    duration: 0,
  });
  const { name, referenceMediaId, animationzz, imageMediaId } =
    mateSprite.value;

  const params: UpdateMateSpriteParams = {
    name,
    referenceMediaId,
    imageMediaId,
    animationzz: animationzz!.map((v) => {
      return {
        id: v.id!,
        mediaId: v.media!.id!,
      };
    }),
  };

  try {
    const { item } = await updateMateSprite(mateSprite.value!.id!, params);
    loading.close();
    const { item: data } = await readMateSprite(item.uuid!);
    mateSprite.value = data;
    useDungeon().fetchMateSpriteList(mateStore.currentMate!.id!, true);
    showSuccessToast(t("sprite.saveSuccess"));

    Object.keys(unsavedDirection).forEach((d) => {
      unsavedDirection[Number(d) as MateSpriteDirection] = false;
    });
    initSort();

    emit("update:show", false);
    emit("saved");
  } catch (e) {
    console.error(e);
    loading.close();
  }
};

// const deleteSprite = async () => {
//   showConfirmDialog({
//     title: t("characterGenerator.messages.sureDeleteSprite"),
//     confirmButtonText: t("Buttons.delete"),
//     cancelButtonText: t("Buttons.cancel"),
//   })
//     .then(async () => {
//       await deleteMateSprite(props.sprite!.id!);
//       emit("update:show", false);
//       emit("saved");
//     })
//     .catch(() => {
//       // ignore
//     });
// };

const showMediaPreviewPopup = ref(false);
const activeAnimationIndex = ref(0);

const clickAnimationImg = async (animationUrl: string, frameIndex: number) => {
  console.log(animationUrl, frameIndex);
  const urls = await getAllFrameUrls(animationUrl, 48, 192, 96);
  console.log(urls);

  modalStore.openMediaPreviewModal(urls, frameIndex, {
    withDownloadTips: true,
  });
};

const onEditorSaved = () => {
  isGenerating.value = true;

  startCountDown(0.3);
  pollTaskStatus();
  emit("saved");
};
</script>

<template>
  <VanPopup
    position="bottom"
    :show="show"
    @update:show="emit('update:show', $event)"
    class="h-full grid grid-rows-[auto_1fr] font-[Cubic]"
    :close-on-click-overlay="false"
  >
    <!-- Header -->
    <ThreadsNav :show-center="false" show-back :go-back="goBack">
      <!-- <template #left>
        <template v-if="sprite">
          <button
            v-if="!sprite?.isDefault"
            class="btn btn-xs rounded btn-outline"
            @click="setDefault"
          >
            {{ $t("Buttons.setDefault") }}
          </button>
          <div
            v-else
            class="flex items-center gap-1 text-xs text-base-content/50"
          >
            <i class="iconify mingcute--check-circle-line" />
            {{ $t("Message.default") }}
          </div>
        </template>
      </template> -->

      <template
        #right
        v-if="
          mateSprite.animationzz?.length ||
          mateSprite.status === MateSpriteStatus.Failed
        "
      >
        <!-- <button
          v-if="mateSprite.id"
          class="btn btn-xs rounded btn-outline"
          @click="deleteSprite"
        >
          {{ $t("Buttons.delete") }}
        </button> -->
        <button
          v-if="mateSprite.animationzz?.length"
          class="btn btn-primary btn-xs rounded"
          @click="clickSave"
        >
          {{ $t("Buttons.save") }}
        </button>
      </template>
    </ThreadsNav>

    <!-- Content -->
    <div class="flex-1 bg-base-100 p-4 overflow-y-auto">
      <!-- Step 3: 生成结果 -->
      <div class="flex flex-col">
        <div class="flex justify-between w-full items-center mb-3 ml-2">
          <h3 class="text-sm text-base-content/70 red-dot">
            {{ $t("sprite.generationResult") }}
          </h3>

          <button
            v-if="!loadingDirection && mateSprite.mode === MateSpriteMode.Image"
            @click="emit('frame-click')"
            class="btn btn-xs btn-outline"
          >
            {{ $t("sprite.regenerateDirection") }}
          </button>
        </div>
        <div
          v-if="isGenerating"
          class="flex flex-col justify-center items-center mt-20 gap-2"
        >
          <div class="loading loading-ring loading-lg"></div>
          {{ fixNumber(countDownPercent, 1, 0) }}%
        </div>
        <template v-else>
          <div class="mb-4">
            <div class="grid grid-cols-4 w-full bg-base-200">
              <div
                v-for="(animation, index) in mateSprite.animationzz"
                :key="animation.mediaId"
                class="flex items-center justify-center px-3 py-1"
                @click="
                  showMediaPreviewPopup = true;
                  activeAnimationIndex = index;
                "
              >
                <SpriteFrame
                  :key="animation.media?.url"
                  :url="animation.media?.url!"
                  class="w-9"
                />
              </div>
            </div>
          </div>

          <!-- Detail Adjustments -->
          <div class="flex-1" v-if="mateSprite.animationzz">
            <h3 class="text-base-content red-dot ml-2 mb-4">
              {{ $t("sprite.detailAdjustment") }}
            </h3>

            <!-- Movement Direction -->
            <div
              class="mb-2 p-2 bg-base-200 rounded-lg"
              v-for="animation in mateSprite.animationzz"
              :key="animation.mediaId"
            >
              <div class="flex justify-between items-center mb-2">
                <h4 class="text-sm text-base-content/70">
                  {{ t("Dungeons.sprite.direction." + animation.direction) }}
                </h4>
                <div class="flex items-center gap-1">
                  <button
                    v-if="!loadingDirection"
                    @click="
                      openSingleFrameEditor(
                        animation.media!.url!,
                        animation.direction
                      )
                    "
                    class="btn btn-xs btn-outline btn-primary"
                  >
                    像素编辑器
                  </button>
                  <button
                    v-if="
                      !loadingDirection &&
                      mateSprite.mode === MateSpriteMode.Image
                    "
                    @click="
                      createTask(animation.spriteId!, animation.direction)
                    "
                    class="btn btn-xs btn-outline"
                  >
                    {{ $t("sprite.regenerateDirection") }}
                    <PowerIcon />
                    1
                  </button>
                </div>
              </div>
              <div
                :class="[
                  mateSprite.mode === MateSpriteMode.Compose
                    ? 'flex overflow-x-auto w-full gap-2'
                    : 'grid grid-cols-4',
                ]"
              >
                <div
                  v-for="(_, idx) in Math.ceil(
                    (animation.media?.width || 192) / 48
                  )"
                  :key="String(animation.mediaId) + idx"
                  class="flex flex-col justify-center items-center flex-shrink-0"
                >
                  <van-skeleton
                    v-if="
                      loadingDirection === animation.direction &&
                      (!loadingFrameIndex || loadingFrameIndex === idx + 1)
                    "
                  >
                    <template #template>
                      <div class="relative w-20 h-[120px]">
                        <div
                          class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center"
                        >
                          <div class="loading loading-ring loading-lg"></div>
                          {{ fixNumber(countDownPercent) }}%
                        </div>
                      </div>
                    </template>
                  </van-skeleton>
                  <template v-else>
                    <SpriteImgList
                      :url="animation.media?.url!"
                      :index="idx"
                      @click="clickAnimationImg(animation.media?.url!, idx)"
                    />
                    <!-- <template v-if="!loadingDirection">
                      <button
                        v-if="mateSprite.mode === MateSpriteMode.Image"
                        class="btn btn-outline btn-xs rounded"
                        @click="
                          clickSingleFrameModify(
                            animation.spriteId!,
                            animation.direction,
                            idx + 1
                          )
                        "
                      >
                        {{ $t("sprite.modifyFrame") }}
                      </button>
                      <button
                        v-if="mateSprite.mode === MateSpriteMode.Compose"
                        class="btn btn-outline btn-xs rounded"
                        @click="
                          showSingleFrameMediaSelectPopup = true;
                          singleFrameDirection = animation.direction;
                          singleFrameFrameIndex = idx + 1;
                        "
                      >
                        {{ $t("sprite.uploadFrame") }}
                      </button>
                    </template> -->
                  </template>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Media Select Popup -->
    <MediaSelectPopup
      v-model:show="showSingleFrameMediaSelectPopup"
      :max-count="1"
      :multiple="false"
      :upload-type="7"
      media-type="img"
      replace
      @on-media-selected="handleSingleFrameImageSelected"
    />

    <VanPopup
      v-model:show="showMediaPreviewPopup"
      v-if="mateSprite.animationzz?.length"
    >
      <VanImagePreview
        :images="mateSprite.animationzz?.map((v) => v.media?.url!)"
        v-model:show="showMediaPreviewPopup"
        :start-position="activeAnimationIndex"
        class="media-preview"
        closeable
        showIndicators
        teleport="body"
        :close-on-click-image="false"
        @close="showMediaPreviewPopup = false"
      >
        <template #image="{ src }">
          <div class="w-full h-full flex justify-center" :key="src">
            <SpriteFrame class="w-40 bg-transparent" :url="src" />
          </div>
        </template>
      </VanImagePreview>
    </VanPopup>
    <CharacterGeneratorPopup
      v-if="mateStore.currentMate?.id"
      v-model:show="showSpriteEditor"
      :sprite="mateSprite"
      :mate-id="mateStore.currentMate.id"
      hide-delete
      @saved="onEditorSaved"
    />

    <ModalSpriteEditor
      v-model:show="showSingleFrameEditor"
      :frame-index="singleFrameFrameIndex ? singleFrameFrameIndex - 1 : 0"
      :image-urls="singleFrameImageUrls"
      @on-confirm="onSingleFrameEditorSaved"
    />
  </VanPopup>
</template>
